import React, { memo, useContext } from 'react';

import { last } from 'ramda';

import { EOrganizedBy } from 'detailsv2/structure';
import useElementSize from 'hooks/useElementSize';

import { PDFDataContext } from '../../context';
import { useLayoutHeight, useRecursive } from '../../functions';
import OrdinaryPage from '../../Templates/OrdinaryPage';
import Blocks from './Blocks';
import { checkPageDevide } from './functions';

function Page({ blocks, onReady = () => {} }) {
  const { page, showImages } = useContext(PDFDataContext);
  const { innerPages, handleReady, handleFail } = useRecursive({
    items: blocks,
    onReady,
    checkPageDevide,
  });

  return (
    <>
      {innerPages.map((p, i) => {
        const currentPageTitle = (() => {
          const hasSubtitle = p?.[0]?.pageTitle?.includes('\n');
          if (page.scheduleType === EOrganizedBy.Day && hasSubtitle) {
            return p?.[0]?.pageTitle?.split('\n')[0];
          }

          return p?.[0]?.pageTitle;
        })();
        const prevPageExists = !!innerPages[i - 1];
        const lastBlockOnPrevPageHasAnotherPageTitle = last(innerPages[i - 1] || [])?.pageTitle !== currentPageTitle;
        const hasImage = showImages && (!prevPageExists || lastBlockOnPrevPageHasAnotherPageTitle);

        return (
          <MemoizedRecursivePage
            key={i}
            blocks={p}
            onFail={handleFail}
            onReady={handleReady}
            title={currentPageTitle}
            isLastPage={i === innerPages.length - 1}
            pageNum={i}
            hasImageInPageTitle={hasImage}
          />
        );
      })}
    </>
  );
}

export default Page;

function BlocksTarget({ isLastPage, blocks, onReady, onFail, size }) {
  const spaceRef = useLayoutHeight({
    maxHeight: size.height - 35,
    ignore: !isLastPage,
    blocks,
    onReady,
    onFail,
  });

  return (
    <div ref={spaceRef}>
      <Blocks items={blocks} />
    </div>
  );
}

export function RecursivePage(props) {
  const { blocks = [], title, subtitle, hasImageInPageTitle } = props;
  const [containerRef, containerSize] = useElementSize();

  const image = hasImageInPageTitle
    ? { url: blocks?.[0]?.image, focalPointCoords: blocks?.[0].primaryImageCoords }
    : undefined;

  return (
    <OrdinaryPage title={title} subtitle={subtitle} image={image}>
      <div style={{ height: '100%', flex: 1 }} ref={containerRef}>
        <BlocksTarget {...props} size={containerSize} />
      </div>
    </OrdinaryPage>
  );
}

export const MemoizedRecursivePage = memo(RecursivePage, (prev, next) => prev.blocks.length === next.blocks.length);
